import * as THREE from "three";
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import guiFactory from './gui';
import { BoxHelper } from "three";

export default function(terminalCanvas, nodesData) {
    
    let scene = new THREE.Scene(),
        camera = new THREE.PerspectiveCamera( 40, window.innerWidth / window.innerHeight, 1, 1000),
        renderer = new THREE.WebGLRenderer(),
        terminalResizeCanvas = document.createElement("canvas"),
        terminalResizeCtx = terminalResizeCanvas.getContext("2d"),
        controls,

        nodes,
        nodeCubes = [],

        terminalMaterial,
        terminalTexture,
        terminalGeometry,
        
        nodeGeometry,
        nodeMaterial,
        
        mouse = new THREE.Vector2(),
        raycaster = new THREE.Raycaster(),
        hoveredNode = null,

        cameraAdjust = new THREE.Vector3(-.5, 1.2, 1.7),
        currentNodeSelection = null,
        moveStart = null,
        previousNode = null,
        currentNode = null,
        moving = false,
        moveCallback = null,

        listeners = []
        ;
        
    function register(listener) {
        listeners.push(listener);
    }

    function resize() {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();

        renderer.setPixelRatio( window.devicePixelRatio );
        renderer.setSize( window.innerWidth, window.innerHeight );
    }

    function mouseMove(event) {
        event.preventDefault();

        mouse.x = ( event.clientX / window.innerWidth ) * 2 - 1;
        mouse.y = - ( event.clientY / window.innerHeight ) * 2 + 1;

        // console.log(mouse);
    }

    function click(event) {
        event.preventDefault();

        if (hoveredNode) {
            currentNodeSelection = hoveredNode;
            listeners.forEach(x => x(currentNodeSelection));
        }
    }

    function moveTo(target, callback) {
        if (!moving) {
            moving = true;
            previousNode = currentNode;
            currentNode = nodes[target.key];
            moveStart = Date.now();
            controls.enabled = false;
            moveCallback = callback;
        }
    }

    function update() {
        if (moving) {
            let delta = (Date.now() - moveStart) / 1000;
            let currentTargetPosition = currentNode.position.clone().add(currentNode.cameraTarget.position);
            if (delta > 1) {
                moving = false;
                controls.target = currentTargetPosition;
                controls.update();
                controls.enabled = true;
                moveCallback();
            } 
            
            let path = currentNode.position.clone().sub(previousNode.position);
            let deltaPath = path.multiplyScalar(Math.min(1, delta));
            let currentPos = previousNode.position.clone().add(deltaPath);
            currentPos = currentPos.add(cameraAdjust);
            camera.position.set(currentPos.x, currentPos.y, currentPos.z);
            camera.lookAt(currentTargetPosition);
        }

        terminalResizeCtx.drawImage(terminalCanvas, 0, 0, 1024, 1024);
        terminalTexture.needsUpdate = true;

        for(let key in nodes) {
            let node = nodes[key];
            let nodeData = nodesData[key];
            node.visible = nodeData.found;
            node.terminal.visible = nodeData.terminalVisible;
            node.cube.rotation.y += .01;
            node.helper.update();
        }

        camera.updateWorldMatrix();
        
        raycaster.setFromCamera( mouse, camera );
        let intersects = raycaster.intersectObjects( nodeCubes );
        if (intersects.length) {
            if (hoveredNode) {
                hoveredNode.helper.visible = false;
                hoveredNode.cube.material.emissive.setHex(0);
            }
            hoveredNode = intersects[0].object.parent;
            hoveredNode.cube.material.emissive.setHex(0x22ff22);
        } else if (hoveredNode) {
            hoveredNode.helper.visible = false;
            hoveredNode.cube.material.emissive.setHex(0);
        }
    }

    function render() {
        renderer.render(scene, camera);

    }

    function createTerminal(x, y, z) {
        let terminal = new THREE.Mesh(terminalGeometry, terminalMaterial);
        terminal.position.set(x, y, z);
        return terminal;
    }

    function createNodeCube(x, y, z, nodeData) {
        let material = nodeMaterial.clone();
        if (nodeData.key === "home") {
            material.color = new THREE.Color(0, 0x80, 0xFF);
        }
        let node = new THREE.Mesh( nodeGeometry, material );
        node.position.set(x, y, z);
        node.rotation.y = Math.random();
        return node;
    }

    function createNode(nodeData) {
        let node = new THREE.Object3D();
        node.name = nodeData.name;
        node.data = nodeData;
        node.position.set(nodeData.position.x, nodeData.position.y, nodeData.position.z);

        node.add(node.terminal = createTerminal(-.5, 1.1, 0));
        node.add(node.cube = createNodeCube(0, 0, 0, nodeData));
        node.cameraTarget = new THREE.Object3D();
        node.cameraTarget.position.set(-.5, 1.05, 0);
        node.add(node.cameraTarget);

        node.helper = new BoxHelper(node.cube, new THREE.Color(255, 255, 255, 1));
        node.helper.visible = false;
        node.add(node.helper);

        let cvs = document.createElement("canvas"),
            ctx = cvs.getContext("2d");
        ctx.width = cvs.width = 256;
        ctx.height = cvs.height = 256/5;
        ctx.font = "43px courier new"
        ctx.fillStyle = "#00FF00";
        let width = ctx.measureText(nodeData.name.toUpperCase()).width;
        let x = 128 - width / 2;
        ctx.fillText(nodeData.name.toUpperCase(), x, 256/10);
        let texture = new THREE.CanvasTexture(cvs);
        let plane = new THREE.PlaneGeometry(1, .1);
        let mesh = new THREE.Mesh(plane, new THREE.MeshBasicMaterial({map:texture, transparent:true, side: THREE.DoubleSide}));
        mesh.position.y = .65;
        node.cube.add(mesh);

        return node;
    }

    function createBackground() {
        let bgCanvas = document.createElement("canvas"),
            bgGui = guiFactory(bgCanvas.getContext("2d")),
            bgTexture = new THREE.CanvasTexture(bgCanvas);
        bgGui.drawBackground();
        scene.background = bgTexture;
    }

    function createHemiLight() {
        let hemiLight = new THREE.HemisphereLight(0xffffff, 0xffffff, 0.6);
        hemiLight.color.setHSL(0.6, 1, 0.6);
        hemiLight.groundColor.setHSL(0.095, 1, 0.75);
        hemiLight.position.set(10, 50, 0);
        scene.add(hemiLight);
    }

    function createControls() {
        controls = new OrbitControls(camera, renderer.domElement);
        controls.maxPolarAngle = Math.PI * 0.5;
        controls.minDistance = 0;
        controls.maxDistance = 10;
    }

    function init() {
        createBackground();
        resize();
        window.addEventListener("resize", resize);
        window.addEventListener("mousemove", mouseMove);
        window.addEventListener("click", click);
    
        renderer.setPixelRatio( window.devicePixelRatio );
    
        terminalResizeCanvas.width = 1024;
        terminalResizeCanvas.height = 1024;
        terminalResizeCanvas.style.width = "1024px";
        terminalResizeCanvas.style.height = "1024px";
        terminalResizeCtx.drawImage(terminalCanvas, 0, 0, 1024, 1024);

        // terminalResizeCtx.fillStyle = "rgb(255, 0, 0)";
        // terminalResizeCtx.fillRect(0, 0, 10, 10);

        terminalGeometry = new THREE.PlaneGeometry(1.92, 1.08, 19, 10);
        terminalTexture = new THREE.CanvasTexture(terminalResizeCanvas);
        terminalMaterial = new THREE.MeshBasicMaterial({map: terminalTexture, transparent: true});
    
        nodeGeometry = new THREE.BoxBufferGeometry( 1, 1, 1 );
        nodeMaterial = new THREE.MeshPhongMaterial( { color: new THREE.Color(0, .8, .1) } );

        nodes = {};
        for(let key in nodesData) {
            let nodeData = nodesData[key];
            let node = createNode(nodeData);
            node.terminal.visible = nodeData.terminalVisible;
            node.visible = nodeData.found;
            nodes[key] = node;
            nodeCubes.push(node.cube);
            scene.add(node);
        }
        currentNode = nodes.home;

        createHemiLight();

        createControls();

        camera.position.set(cameraAdjust.x, cameraAdjust.y, cameraAdjust.z);
        controls.target = nodes.home.cameraTarget.position;
        controls.update();
    
        // document.body.appendChild(canvas);
        document.body.appendChild(renderer.domElement);
    }

    return {
        init,
        update,
        render,
        currentTarget: currentNodeSelection,
        moveTo,
        register
    }

}